import React, {useEffect, useState, useMemo} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, FormattedNumber} from 'react-intl'
import {
    Box,
    Flex,
    Button,
    Stack,
    Text,
    Heading,
    Divider,
    Accordion,
    AccordionIcon,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    Image
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import {PromoCode, usePromoCode} from '../promo-code/index'
import PromoPopover from '../promo-popover/index'
import {useProducts, useShopperBasketsMutation} from '@salesforce/commerce-sdk-react'
import {
    currencyStyle,
    editCartButtonStyle,
    editCartItemStyle,
    estimatedCurrencyStyle,
    estimatedTotalStyle,
    freeTextStyle,
    giftCardStyle,
    noteStyle,
    orderSummaryHeading,
    salesTaxCurrencyStyle,
    salesTaxStyle,
    shippingCurrencyStyle,
    shippingStyle,
    subtotalStyle,
    tbdStyle,
    dividerStyle,
    productItemStyles
} from './index.style'
import {transformBasketData, transformGiftCertificateItems} from '../../utills/transformBasket'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {extractCouponsFromBasket} from '../../utills/couponUtils'
import useSitePreference from '../../hooks/use-site-preference'
import ProductItem from '../product-item'
import {pxToRem} from '../../utills/pxToRem'

const CartItems = ({basket}) => {
    const {data: statesMappingData} = useSitePreference('statesMapping')
    const statesMapping = statesMappingData?.statesMapping || {}
    const totalItems = basket?.productItems?.reduce((acc, item) => acc + item.quantity, 0) || 0
    const productIds = basket?.productItems?.map(({productId}) => productId).join(',') ?? ''
    const {data: products, isLoading: isProductsLoading} = useProducts(
        {
            parameters: {
                ids: productIds,
                allImages: true,
                perPricebook: true,
                c_storepickup: true,
                c_isPDPRequest: true
            }
        },
        {
            enabled: Boolean(productIds),
            select: (result) => {
                return result?.data?.reduce((result, item) => {
                    const key = item.id
                    result[key] = item
                    return result
                }, {})
            }
        }
    )

    const getCurrentQuantity = (product) => {
        return product?.quantity
    }

    const bundleChildVariantIds = []
    basket?.productItems?.forEach((productItem) => {
        productItem?.bundledProductItems?.forEach((childProduct) => {
            bundleChildVariantIds.push(childProduct.productId)
        })
    })

    const {data: bundleChildProductData} = useProducts(
        {
            parameters: {
                ids: bundleChildVariantIds?.join(','),
                allImages: false,
                expand: ['availability', 'variations'],
                select: '(data.(id,inventory))'
            }
        },
        {
            enabled: bundleChildVariantIds?.length > 0,
            keepPreviousData: true,
            select: (result) => {
                return result?.data?.reduce((result, item) => {
                    const key = item.id
                    result[key] = item
                    return result
                }, {})
            }
        }
    )

    const productsByItemId = useMemo(() => {
        const updateProductsByItemId = {}
        basket?.productItems?.forEach((productItem) => {
            let currentProduct = products?.[productItem?.productId]

            if (productItem?.bundledProductItems && bundleChildProductData) {
                let lowestStockLevel =
                    currentProduct?.inventory?.stockLevel ?? Number.MAX_SAFE_INTEGER
                let productWithLowestInventory = ''
                productItem?.bundledProductItems.forEach((bundleChild) => {
                    const bundleChildStockLevel =
                        bundleChildProductData?.[bundleChild.productId]?.inventory?.stockLevel ??
                        Number.MAX_SAFE_INTEGER
                    lowestStockLevel = Math.min(lowestStockLevel, bundleChildStockLevel)
                    if (lowestStockLevel === bundleChildStockLevel)
                        productWithLowestInventory = bundleChild.productName
                })

                if (currentProduct?.inventory) {
                    currentProduct = {
                        ...currentProduct,
                        inventory: {
                            ...currentProduct.inventory,
                            stockLevel: lowestStockLevel,
                            lowestStockLevelProductName: productWithLowestInventory
                        }
                    }
                }
            }
            updateProductsByItemId[productItem.itemId] = currentProduct
        })
        return updateProductsByItemId
    }, [basket, products, bundleChildProductData])

    const transformedProductData = transformBasketData(products, basket, statesMapping)
    const transformedGiftCertificates = transformGiftCertificateItems(basket.giftCertificateItems)

    const allTransformedItems = [...transformedProductData, ...transformedGiftCertificates]

    return (
        <Accordion allowToggle={true} width="100%">
            <AccordionItem style={{border: 0}}>
                <AccordionButton padding="0px" sx={editCartItemStyle}>
                    <Image src={getAssetUrl('static/img/cart/cart-icon.svg')} />
                    <Box px={2}>
                        <FormattedMessage
                            id="order_summary.cart_items.action.num_of_items_in_cart"
                            description="clicking it would expand/show the items in cart"
                            defaultMessage="{itemCount, plural, =0 {0 items} one {# item} other {# items}} in cart"
                            values={{
                                itemCount: totalItems + (transformedGiftCertificates?.length || 0)
                            }}
                        />
                    </Box>
                    <AccordionIcon />
                </AccordionButton>
                <AccordionPanel px={0} py={4}>
                    <Stack spacing={0}>
                        {allTransformedItems?.map((transformedItem, idx) => {
                            const productItem = transformedItem.isGiftCertificate
                                ? transformedItem
                                : transformedItem.basketItem || transformedItem

                            if (!productItem && !transformedItem.isGiftCertificate) return null

                            const currentQuantity = transformedItem.isGiftCertificate
                                ? 1
                                : getCurrentQuantity(transformedItem.basketItem || transformedItem)

                            const currentProduct = transformedItem.isGiftCertificate
                                ? {
                                      ...transformedItem,
                                      itemId: transformedItem.id,
                                      productId: transformedItem.id,
                                      quantity: currentQuantity,
                                      isGiftCertificate: true,
                                      transformedProducts: allTransformedItems
                                  }
                                : {
                                      ...productItem,
                                      ...(productsByItemId && productsByItemId[productItem.itemId]),
                                      isProductUnavailable: !isProductsLoading
                                          ? !productsByItemId?.[productItem.itemId]
                                          : undefined,
                                      price: productItem.price,
                                      quantity: currentQuantity,
                                      transformedProducts: allTransformedItems,
                                      ...transformedItem
                                  }

                            return (
                                <React.Fragment key={transformedItem.itemId || transformedItem.id}>
                                    <Box
                                        position="relative"
                                        sx={{
                                            mb: {base: '10px', lg: '12.5px'},
                                            px: {base: pxToRem(11)}
                                        }}
                                    >
                                        <ProductItem
                                            index={idx}
                                            product={currentProduct}
                                            customStyles={productItemStyles}
                                            isCart={false}
                                        />
                                    </Box>
                                    <Box sx={{mb: {base: '10px', lg: '12.5px'}}}>
                                        <Divider sx={dividerStyle} />
                                    </Box>
                                </React.Fragment>
                            )
                        })}

                        <Button as={Link} to="/cart" variant="link" sx={editCartButtonStyle}>
                            <FormattedMessage
                                defaultMessage="Edit cart"
                                id="order_summary.cart_items.link.edit_cart"
                            />
                        </Button>
                    </Stack>
                </AccordionPanel>
            </AccordionItem>
        </Accordion>
    )
}

CartItems.propTypes = {
    basket: PropTypes.object
}

const OrderSummary = ({
    basket,
    showPromoCodeForm = false,
    showCartItems = false,
    isEstimate = false,
    totalGiftCardValue = 0,
    hideNote = false,
    addDivider = false,
    onBasketUpdate,
    isCart = false
}) => {
    const {removePromoCode, ...promoCodeProps} = usePromoCode()

    const [removedCoupons, setRemovedCoupons] = useState(new Set())

    const updateBasket = useShopperBasketsMutation('updateBasket')

    useEffect(() => {
        setRemovedCoupons(new Set())
    }, [basket?.basketId, basket?.orderNo])

    const removeAllLastModified = (obj) => {
        if (!obj || typeof obj !== 'object') return obj

        if (Array.isArray(obj)) {
            return obj.map(removeAllLastModified)
        }

        const result = {}
        for (const key in obj) {
            if (key === 'lastModified') continue // skip this key
            result[key] = removeAllLastModified(obj[key]) // recurse for nested objects/arrays
        }
        return result
    }

    useEffect(() => {
        if (basket?.basketId) {
            const bodyParam = isCart ? {c_isCart: true} : {c_isCheckout: true}
            updateBasket.mutate({
                parameters: {basketId: basket.basketId},
                body: bodyParam
            })
        }
    }, [JSON.stringify(removeAllLastModified(basket)), isCart])

    if (!basket?.basketId && !basket?.orderNo) {
        return null
    }

    const shippingItem = basket.shippingItems?.[0]
    const hasShippingPromos = shippingItem?.priceAdjustments?.length > 0

    const finalTotal =
        (basket?.orderTotal ? basket?.orderTotal : basket?.c_estimatedTotal) - totalGiftCardValue

    const handleRemovePromoCode = async (couponId) => {
        try {
            setRemovedCoupons((prev) => new Set([...prev, couponId]))
            await removePromoCode(couponId)
            if (onBasketUpdate) {
                await onBasketUpdate()
            }
        } catch (error) {
            setRemovedCoupons((prev) => {
                const newSet = new Set(prev)
                newSet.delete(couponId)
                return newSet
            })
        }
    }

    let totalDiscount = null
    if (basket?.orderPriceAdjustments && !basket?.orderPriceAdjustments?.[0]?.couponCode) {
        totalDiscount = basket?.orderPriceAdjustments?.[0]?.price
    }

    return (
        <Stack data-testid="sf-order-summary" spacing={5}>
            <Heading id="order-summary-heading" sx={orderSummaryHeading}>
                <FormattedMessage
                    defaultMessage="Order Summary"
                    id="order_summary.heading.order_summary"
                />
            </Heading>

            <Stack
                spacing={4}
                marginTop="0px !important"
                align="flex-start"
                role="region"
                aria-labelledby="order-summary-heading"
            >
                {showCartItems && <CartItems basket={basket} />}

                <Stack w="full" marginTop="0px !important">
                    <Flex justify="space-between" aria-live="polite" aria-atomic="true">
                        <Text sx={subtotalStyle}>
                            <FormattedMessage
                                defaultMessage="Subtotal"
                                id="order_summary.label.subtotal"
                            />
                        </Text>
                        <Text sx={currencyStyle}>
                            <FormattedNumber
                                style="currency"
                                currency={basket?.currency}
                                value={basket?.c_orderTotal}
                            />
                        </Text>
                    </Flex>

                    {/* gift card */}
                    {totalGiftCardValue > 0 && (
                        <Flex justify="space-between" aria-live="polite" aria-atomic="true">
                            <Text sx={giftCardStyle}>
                                <FormattedMessage
                                    defaultMessage="Gift Card"
                                    id="order_summary.label.gift_card_discount"
                                />
                            </Text>
                            <Text as="span" sx={giftCardStyle}>
                                -{' '}
                                <FormattedNumber
                                    style="currency"
                                    currency={basket?.currency}
                                    value={totalGiftCardValue}
                                />
                            </Text>
                        </Flex>
                    )}

                    <Flex justify="space-between" aria-live="polite" aria-atomic="true">
                        <Flex alignItems="center">
                            <Text sx={shippingStyle}>
                                <FormattedMessage
                                    defaultMessage="Shipping"
                                    id="order_summary.label.shipping"
                                />
                            </Text>
                            {hasShippingPromos && (
                                <Text as="span" sx={shippingStyle}>
                                    (
                                    <FormattedMessage
                                        defaultMessage="Promotion applied"
                                        id="order_summary.label.promo_applied"
                                    />
                                    )
                                </Text>
                            )}
                            {hasShippingPromos && (
                                <PromoPopover>
                                    <Stack>
                                        {shippingItem?.priceAdjustments?.map((adjustment) => (
                                            <Text key={adjustment.priceAdjustmentId}>
                                                {adjustment.itemText}
                                            </Text>
                                        ))}
                                    </Stack>
                                </PromoPopover>
                            )}
                        </Flex>

                        {shippingItem?.priceAdjustments?.some(
                            ({appliedDiscount}) => appliedDiscount?.type === 'free'
                        ) ? (
                            <Text as="span" sx={freeTextStyle}>
                                <FormattedMessage
                                    defaultMessage="Free"
                                    id="order_summary.label.free"
                                />
                            </Text>
                        ) : (
                            <Text sx={shippingCurrencyStyle}>
                                <FormattedNumber
                                    value={basket.shippingTotal}
                                    style="currency"
                                    currency={basket.currency}
                                />
                            </Text>
                        )}
                    </Flex>

                    <Flex justify="space-between" aria-live="polite" aria-atomic="true">
                        <Text sx={salesTaxStyle}>
                            <FormattedMessage
                                defaultMessage="Sales Tax"
                                id="order_summary.label.sales_tax"
                            />
                        </Text>
                        {basket.taxTotal != null ? (
                            <Text sx={salesTaxCurrencyStyle}>
                                <FormattedNumber
                                    value={basket.taxTotal}
                                    style="currency"
                                    currency={basket.currency}
                                />
                            </Text>
                        ) : (
                            <Text sx={tbdStyle}>-</Text>
                        )}
                    </Flex>
                    {!hideNote && (
                        <Text sx={noteStyle}>
                            <FormattedMessage defaultMessage="Shipping and taxes calculated at checkout" />
                        </Text>
                    )}

                    <Flex justify="space-between" aria-live="polite" aria-atomic="true">
                        <Text sx={salesTaxStyle}>
                            <FormattedMessage
                                defaultMessage="Discount"
                                id="order_summary.label.discount"
                            />
                        </Text>
                        {totalDiscount != null ? (
                            <Text sx={salesTaxCurrencyStyle}>
                                <FormattedNumber
                                    value={totalDiscount}
                                    style="currency"
                                    currency={basket.currency}
                                />
                            </Text>
                        ) : (
                            <Text sx={tbdStyle}>-</Text>
                        )}
                    </Flex>
                </Stack>

                {showPromoCodeForm && (
                    <Box w="full">
                        <PromoCode {...promoCodeProps} removePromoCode={handleRemovePromoCode} />
                    </Box>
                )}

                {addDivider && <Divider borderColor="rgba(0, 0, 0, 0.30)" />}
                <Stack marginTop="0px !important" w="full">
                    <Flex w="full" justify="space-between" aria-live="polite" aria-atomic="true">
                        {isEstimate ? (
                            <Text sx={estimatedTotalStyle}>
                                <FormattedMessage
                                    defaultMessage="Est. Total"
                                    id="order_summary.label.est_total"
                                />
                            </Text>
                        ) : (
                            <Text sx={estimatedTotalStyle}>
                                <FormattedMessage
                                    defaultMessage="Order Total"
                                    id="order_summary.label.order_total"
                                />
                            </Text>
                        )}
                        <Text sx={estimatedCurrencyStyle}>
                            <FormattedNumber
                                style="currency"
                                currency={basket?.currency}
                                value={finalTotal}
                            />
                        </Text>
                    </Flex>
                </Stack>
            </Stack>
        </Stack>
    )
}

OrderSummary.propTypes = {
    basket: PropTypes.object,
    showPromoCodeForm: PropTypes.bool,
    showCartItems: PropTypes.bool,
    isEstimate: PropTypes.bool,
    totalGiftCardValue: PropTypes.number,
    fontSize: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
    hideNote: PropTypes.bool,
    onBasketUpdate: PropTypes.func,
    isCart: PropTypes.bool,
    addDivider: PropTypes.bool
}

export default OrderSummary
